import { useState } from "react";
import { useTranslation } from "react-i18next";
import LanguageToggle from "@/components/LanguageToggle";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, Bot, BookOpen, GitBranch,
  MessageSquare, Settings, ChevronLeft, ChevronRight, Building2, LogOut, User, Users, Smartphone, CreditCard, LifeBuoy
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const navItems = [
  { path: "/", icon: LayoutDashboard, labelKey: "nav.dashboard" },
  { path: "/businesses", icon: Building2, labelKey: "nav.businesses", adminOnly: true },
  { path: "/whatsapp", icon: Smartphone, labelKey: "nav.whatsapp" },
  { path: "/agency", icon: Building2, labelKey: "nav.agency", nonAdminOnly: true },
  { path: "/agents", icon: Bot, labelKey: "nav.agents" },
  { path: "/knowledge-base", icon: BookOpen, labelKey: "nav.knowledge" },
  { path: "/workflows", icon: GitBranch, labelKey: "nav.workflows" },
  { path: "/conversations", icon: MessageSquare, labelKey: "nav.conversations" },
  { path: "/users", icon: Users, labelKey: "nav.users", adminOnly: true },
  { path: "/saas-admin", icon: CreditCard, labelKey: "nav.saasAdmin", adminOnly: true },
  { path: "/settings", icon: Settings, labelKey: "nav.settings" },
];

export const Sidebar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, brand } = useAuth();
  const { t } = useTranslation();

  const supportUrl = `https://system.mktscaled.com/widget/form/VMeJjA9TM1QjB8ZliTA2?email=${encodeURIComponent(user?.email || "")}&first_name=${encodeURIComponent(user?.name || "")}`;

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <aside
      data-testid="sidebar"
      className={cn(
        "h-screen flex flex-col border-r border-border bg-white shadow-sm transition-[width] duration-300",
        collapsed ? "w-[68px]" : "w-[240px]"
      )}
    >
      <div className="flex items-center gap-3 px-4 h-16 border-b border-border">
        <img
          src={brand?.logo_url || "/isotipo.png"}
          alt={brand?.name || "LeadBot"}
          className="w-8 h-8 rounded-lg flex-shrink-0 object-cover"
        />
        {!collapsed && (
          <span className="font-['Manrope'] font-extrabold text-lg text-foreground tracking-tight">
            {brand?.name || "LeadBot"}
          </span>
        )}
        {!collapsed && <div className="ml-auto"><LanguageToggle /></div>}
      </div>

      <nav className="flex-1 py-4 px-2 space-y-1">
        {navItems.map(({ path, icon: Icon, labelKey, adminOnly, nonAdminOnly }) => {
          // Skip admin-only items for non-admins (admin role = super_admin)
          if (adminOnly && user?.role !== "admin") return null;
          if (nonAdminOnly && user?.role === "admin") return null;
          // Embed/restricted users (GHL SaaS clients and sub-account end clients)
          // never buy plans themselves — hide the reseller panel from them.
          if (nonAdminOnly && (user?.email || "").endsWith("@leadbot.embed")) return null;
          
          const isActive = path === "/"
            ? location.pathname === "/"
            : location.pathname.startsWith(path);

          return (
            <NavLink
              key={path}
              to={path}
              data-testid={`nav-${labelKey.split(".")[1]}`}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors duration-200 group",
                isActive
                  ? "bg-white/5 text-white"
                  : "text-muted-foreground hover:bg-white/5 hover:text-foreground"
              )}
            >
              <Icon className={cn(
                "w-5 h-5 flex-shrink-0 transition-colors duration-200",
                isActive ? "text-primary" : "group-hover:text-foreground"
              )} />
              {!collapsed && (
                <span className="text-sm font-medium truncate">{t(labelKey)}</span>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Support */}
      <div className="px-2 pb-1">
        <button
          data-testid="support-btn"
          onClick={() => setSupportOpen(true)}
          className={cn(
            "flex items-center gap-3 px-3 py-2.5 rounded-lg w-full text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors duration-200",
            collapsed ? "justify-center" : ""
          )}
        >
          <LifeBuoy className="w-5 h-5 flex-shrink-0" />
          {!collapsed && <span className="text-sm font-medium">{t("nav.support")}</span>}
        </button>
      </div>

      {/* User section */}
      <div className="border-t border-border px-2 py-3">
        {user && (
          <div className={cn(
            "flex items-center gap-3 px-3 py-2 rounded-lg",
            collapsed ? "justify-center" : ""
          )}>
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
              <User className="w-4 h-4 text-primary" />
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{user.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user.email}</p>
              </div>
            )}
          </div>
        )}
        <button
          data-testid="logout-btn"
          onClick={handleLogout}
          className={cn(
            "flex items-center gap-3 px-3 py-2 rounded-lg w-full text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors duration-200",
            collapsed ? "justify-center" : ""
          )}
        >
          <LogOut className="w-4 h-4 flex-shrink-0" />
          {!collapsed && <span className="text-sm">{t("nav.logout")}</span>}
        </button>
      </div>

      <button
        data-testid="sidebar-toggle"
        onClick={() => setCollapsed(!collapsed)}
        className="flex items-center justify-center h-12 border-t border-border text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors duration-200"
      >
        {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
      </button>

      <Dialog open={supportOpen} onOpenChange={setSupportOpen}>
        <DialogContent className="max-w-lg p-0 overflow-hidden">
          <DialogHeader className="px-5 pt-5 pb-2">
            <DialogTitle>Levantar ticket de soporte</DialogTitle>
          </DialogHeader>
          <iframe
            src={supportOpen ? supportUrl : "about:blank"}
            style={{ width: "100%", height: "70vh", border: "none" }}
            title="Ticket de soporte"
          />
        </DialogContent>
      </Dialog>
    </aside>
  );
};
